import { useEffect, useState } from 'react'
import { AlertTriangle, Banknote, ChevronDown, CloudOff, CreditCard, Globe, RefreshCw, Wallet } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn, formatTimeHuman } from '@/lib/utils'
import { formatMoney } from '@/lib/currency'
import { listOfflineOrders, type OfflineOrder } from '@/lib/offline/offlineStore'

/**
 * Why an entry has not synced yet, in words a cashier can act on (F-FE-N78).
 *
 * `lastError` was recorded on every failed attempt and shown nowhere, so a
 * queue blocked on something a human could fix in one tap — most often a
 * business day nobody has opened — looked identical to one that was simply
 * waiting for the network. "Pending sync" for twenty minutes with no reason is
 * what makes an owner reload the app and conclude the feature is broken.
 */
function stuckReason(o: OfflineOrder): string | null {
  if (o.status !== 'pending' || o.attempts === 0) return null
  switch (o.lastError) {
    case 'day_not_open':
      return 'Waiting — open the business day to sync this'
    case 'previous_day_open':
      return "Waiting — close yesterday's business day to sync this"
    case 'network_error':
      return 'Waiting for the connection'
    case null:
    case undefined:
      return null
    default:
      return `Retrying — ${o.lastError}`
  }
}

/**
 * The cashier's view of orders held on this device (F-FE-N30).
 *
 * Additive only. Nothing here can cancel, void or reprice a held order — the
 * food has already been fired, so the local queue must never be a way to make
 * it disappear.
 *
 * F-FE-N77 rebuilt this as CARDS with the two actions service actually needs on
 * a held ticket, because the first cut of offline payment put payment in a modal
 * that opened by itself the moment an order fired. That blocked everything: you
 * could not start the next order, could not look at another ticket, and on a
 * short window could not even reach its buttons. Now it mirrors the Open Orders
 * strip that already works online — tap a card to load it, add the table's next
 * round, take payment when the guest is ready, tap again to put it down and pick
 * up another.
 *
 * Unreconciled rows stay inert. Those were fired but could not be billed, and
 * appending to work a manager has to write off would only grow the problem.
 *
 * Polls rather than subscribes: the store is local IndexedDB with no change
 * events, and a few seconds of staleness costs nothing here.
 */
export function OfflineOrdersTray({
  refreshKey,
  selectedOpId,
  onSelect,
  onTakePayment,
  priceOf,
}: {
  refreshKey?: number
  /** The held order currently loaded in the rail, if any. */
  selectedOpId?: string | null
  /** Omit to render the tray read-only (e.g. where there is no rail to load into). */
  onSelect?: (o: OfflineOrder) => void
  /** Omit to hide the pay action (read-only surfaces). */
  onTakePayment?: (o: OfflineOrder) => void
  /**
   * Current value of a held order, re-derived from the cached menu. Omit to
   * hide amounts. A queue entry stores no prices by design, so this is computed,
   * never read back — see lib/offline/priceOfflineOrder.ts.
   */
  priceOf?: (o: OfflineOrder) => number
}) {
  const [orders, setOrders] = useState<OfflineOrder[]>([])
  /**
   * Collapsed by default (F-FE-N160) — see the summary bar below for why.
   *
   * Deliberately NOT persisted. The tray only appears during and just after an
   * outage, and a cashier who left it open on a previous shift should not
   * inherit a squeezed cart at the start of the next one.
   */
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const all = await listOfflineOrders()
      // `discarded` is terminal with nothing to bill (F-FE-N160) — it stays in
      // IndexedDB for its `voids` audit trail but must leave the cashier's
      // screen, exactly as `synced` does. Leaving it visible would reproduce the
      // un-removable card this fix exists to end.
      if (!cancelled) {
        setOrders(all.filter((o) => o.status !== 'synced' && o.status !== 'discarded'))
      }
    }
    void load()
    const t = window.setInterval(load, 5000)
    return () => {
      cancelled = true
      window.clearInterval(t)
    }
  }, [refreshKey])

  if (orders.length === 0) return null

  const pending = orders.filter((o) => o.status === 'pending')
  const stuck = orders.filter((o) => o.status === 'unreconciled')
  const unpaid = pending.filter((o) => !o.payment).length

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3 dark:border-amber-800 dark:bg-amber-950/30">
      {/* F-FE-N160: the summary bar is a BUTTON and the tray starts collapsed.

          This block lives in the ticket rail's trailing `auto` grid row, beside
          the tax preview and the primary action. That row takes whatever height
          it asks for, and the cart list above it is the `minmax(0,1fr)` row that
          gives way — so an expanded tray squeezed the cart to a sliver and items
          the cashier rang appeared to vanish "behind" it (Mizaj, 2026-09-23).
          Collapsed this is ~44px instead of ~260px, and the cart keeps the room.

          Collapsed is the right default because the bar already carries the two
          facts that matter at a glance: how many are held, and how many are
          unpaid. The cards are only needed when acting on one. */}
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
          <CloudOff className="h-4 w-4 shrink-0" />
          <span className="truncate">Offline orders ({orders.length})</span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {unpaid > 0 && onTakePayment != null && (
            <span className="text-xs text-amber-900/80 dark:text-amber-200/80">
              {unpaid} unpaid
            </span>
          )}
          <ChevronDown
            aria-hidden
            className={cn(
              'h-4 w-4 shrink-0 text-amber-900/70 transition-transform dark:text-amber-200/70',
              expanded && 'rotate-180',
            )}
          />
        </span>
      </button>

      {expanded && (
        <>
      {/* Capped and scrollable, so that even expanded — and even with several
          held orders — the footer can never grow without bound. */}
      <ul className="mt-2 grid max-h-[28vh] gap-2 overflow-y-auto overscroll-contain sm:grid-cols-2">
        {[...pending, ...stuck].map((o) => {
          const selectable = o.status === 'pending' && onSelect != null
          const selected = selectable && selectedOpId === o.opId
          const paid = o.payment != null
          const amount = priceOf?.(o)

          return (
            <li key={o.opId}>
              <div
                className={cn(
                  'rounded-md border bg-background/70 p-2.5 transition-colors dark:bg-background/40',
                  selected
                    ? 'border-amber-500 ring-1 ring-amber-500'
                    : 'border-amber-200 dark:border-amber-900',
                )}
              >
                {/* The card body is the select target. Buttons live outside it
                    so a tap meant for "Pay" never also loads the ticket. */}
                <button
                  type="button"
                  aria-pressed={selected}
                  disabled={!selectable}
                  onClick={() => selectable && onSelect?.(o)}
                  className={cn(
                    'w-full text-left',
                    selectable ? 'cursor-pointer' : 'cursor-default',
                  )}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate font-mono text-sm font-semibold">
                      {o.ticketRef || o.orderNumber}
                    </span>
                    {amount != null && (
                      <span className="shrink-0 text-sm font-semibold tabular-nums">
                        {formatMoney(amount)}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span className="truncate">
                      {o.lines.length} item{o.lines.length === 1 ? '' : 's'}
                      {o.tableId ? ' · table' : ''}
                    </span>
                    <span className="shrink-0 tabular-nums">
                      {formatTimeHuman(o.capturedAt)}
                    </span>
                  </div>
                </button>

                <div className="mt-2 flex items-center justify-between gap-2">
                  {o.status === 'pending' ? (
                    paid ? (
                      <Badge variant="secondary" className="gap-1">
                        {o.payment?.method === 'cash' ? (
                          <Banknote className="h-3 w-3" />
                        ) : o.payment?.method === 'online' ? (
                          <Globe className="h-3 w-3" />
                        ) : (
                          <CreditCard className="h-3 w-3" />
                        )}
                        Paid · {o.payment?.method}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="gap-1">
                        <RefreshCw className="h-3 w-3" />
                        Pending sync
                      </Badge>
                    )
                  ) : (
                    <Badge variant="destructive" className="gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Needs a manager
                    </Badge>
                  )}

                  {o.status === 'pending' && !paid && onTakePayment != null && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => onTakePayment(o)}
                      className="h-7 gap-1.5 px-2 text-xs"
                    >
                      <Wallet className="h-3.5 w-3.5" />
                      Pay
                    </Button>
                  )}
                </div>

                {stuckReason(o) && (
                  <p className="mt-1.5 text-[11px] leading-snug text-amber-800 dark:text-amber-300">
                    {stuckReason(o)}
                  </p>
                )}
              </div>
            </li>
          )
        })}
      </ul>

      {pending.length > 0 && onSelect != null && (
        <p className="mt-2 text-xs text-amber-900/80 dark:text-amber-200/80">
          Tap a card to add the table&apos;s next round to it; tap it again to put it down.
        </p>
      )}

        </>
      )}

      {/* Deliberately OUTSIDE the collapse (F-FE-N160): this is the one thing in
          the tray that is not merely informational. These orders were fired and
          could not be billed, and Day Close is blocked until a manager resolves
          them — so it must not be possible to hide it by folding the tray away. */}
      {stuck.length > 0 && (
        <p className="mt-2 text-xs text-amber-900 dark:text-amber-200">
          {stuck.length} order{stuck.length === 1 ? ' was' : 's were'} sent to the kitchen
          but could not be billed. A manager must resolve {stuck.length === 1 ? 'it' : 'them'}{' '}
          in Day Close before the day can be closed.
        </p>
      )}
    </div>
  )
}
